'use client';

import { useState } from 'react';
import posthog from 'posthog-js';
import { Check, Copy } from 'lucide-react';
import { SQUAD_COMMISSION_CAP_USD } from '@peach/types/squad-member/commission-cap.js';
import { getReferralLink } from '@/app/actions/squad';
import { matePath } from '@/lib/utils/mate-link';

/**
 * A provider reading another provider's page gets their referral link to them
 * here, where they already know who they are recommending. The squad is minted
 * on the first tap; every later tap reads the same alias back.
 */
export function ReferCard({
  tutorUsername,
  tutorName,
}: {
  tutorUsername: string;
  tutorName: string;
}) {
  const [alias, setAlias] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${matePath(value)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      posthog.capture('referral_link_copied', {
        tutor_username: tutorUsername,
        provider_username: tutorUsername,
        creator_username: tutorUsername,
        surface: 'storefront',
      });
    } catch {
      // Denied clipboard: the address is on screen to select by hand.
    }
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    const result = await getReferralLink(tutorUsername);
    setBusy(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    setAlias(result.data.alias);
    await copy(result.data.alias);
  };

  return (
    <div className="rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3">
      <span className="block sf-title text-title-2 font-extrabold text-[var(--sf-ink)]">
        Refer {tutorName}
      </span>
      <span className="sf-note mt-1 block">
        Send your clients to {tutorName} with your link. You earn 20% of what they buy, up to $
        {SQUAD_COMMISSION_CAP_USD} per client.
      </span>

      {error && <span className="sf-note mt-1 block text-error">{error}</span>}

      {alias ? (
        <button
          type="button"
          onClick={() => void copy(alias)}
          aria-label={copied ? 'Link copied' : `Copy referral link to ${tutorName}`}
          className="mm-btn mt-2 flex w-full items-center justify-between gap-3 rounded-xl bg-[var(--sf-ticket)] px-3.5 py-2.5 text-left"
        >
          <span className="truncate text-p-3 font-semibold text-[var(--sf-ink)]">
            meetmy.io{matePath(alias)}
          </span>
          {copied ? (
            <Check aria-hidden className="size-4 shrink-0 text-[var(--sf-ink)]" />
          ) : (
            <Copy aria-hidden className="size-4 shrink-0 text-[var(--sf-mut)]" />
          )}
        </button>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void start()}
          className="mm-btn sf-cta sf-cta-ink mt-2"
        >
          {busy ? 'Getting your link…' : 'Copy referral link'}
        </button>
      )}
    </div>
  );
}
